import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { IMAGERY } from "../lib/imagery";
import HeroCarousel from "../components/HeroCarousel";
import { accent } from "../lib/accent";
import { trackEvent } from "../lib/analytics";

export default function Home() {
  const { t } = useLang();

  const slides = [
    {
      image: IMAGERY.rioCity,
      // Image is now a panoramic 16:9 crop, no contain mode needed.
      eyebrow: t.home.slide1Eyebrow,
      title: t.home.slide1Title,
      sub: t.home.slide1Sub,
      alt: "Rio de Janeiro skyline at night, Sugarloaf and Botafogo Bay",
    },
    {
      image: IMAGERY.spFinancialDusk,
      filter: "brightness(0.78) contrast(1.06) saturate(0.95)",
      eyebrow: t.home.slide4Eyebrow,
      title: t.home.slide4Title,
      sub: t.home.slide4Sub,
      alt: "Aerial view of São Paulo financial district at dusk",
      ctaPrimaryLabel: t.home.slide4CtaPrimary,
      ctaPrimaryTo: "/contact",
      ctaSecondaryLabel: t.home.slide4CtaSecondary,
      ctaSecondaryTo: "/about",
    },
    {
      image: IMAGERY.resourcesFusion,
      filter: "brightness(0.82) contrast(1.08) saturate(1.02)",
      eyebrow: t.home.slide5Eyebrow,
      title: t.home.slide5Title,
      sub: t.home.slide5Sub,
      alt: "Deepwater FPSO platform fusing into Brazilian cerrado farmland aerial",
      ctaPrimaryLabel: t.home.slide5CtaPrimary,
      ctaPrimaryTo: "/sectors",
      ctaSecondaryLabel: t.home.slide5CtaSecondary,
      ctaSecondaryTo: "/sectors",
    },
    {
      image: IMAGERY.energyFuture,
      filter: "brightness(0.78) contrast(1.06) saturate(0.95)",
      eyebrow: t.home.slide6Eyebrow,
      title: t.home.slide6Title,
      sub: t.home.slide6Sub,
      subItalic: t.home.slide6SubItalic,
      accent: "#4CA8C9",
      verticalAlign: "top",
      alt: "Offshore wind turbines emerging from Atlantic horizon at dawn with transmission grid blueprint overlay",
      ctaPrimaryLabel: t.home.slide6CtaPrimary,
      ctaPrimaryTo: "/sectors",
      ctaSecondaryLabel: t.home.slide6CtaSecondary,
      ctaSecondaryTo: "/solutions",
    },
    {
      image: IMAGERY.energyPump,
      filter: "brightness(0.85) contrast(1.05)",
      eyebrow: t.home.slide3Eyebrow,
      title: t.home.slide3Title,
      sub: t.home.slide3Sub,
      alt: "Oil pumpjack silhouette at sunset",
    },
  ];

  return (
    <>
      <HeroCarousel slides={slides} t={t} />

      {/* QUOTE STRIP */}
      <section
        data-testid="quote-strip"
        className="relative bg-obsidian-700 border-y border-white/5 overflow-hidden"
      >
        <div className="container-x py-24 md:py-32 relative z-10">
          <div className="max-w-5xl mx-auto text-center relative">
            <span className="block w-12 h-px bg-champagne mx-auto mb-10 relative z-10" />
            <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl text-white leading-[1.35] tracking-tight relative z-10 max-w-3xl mx-auto">
              <span aria-hidden="true" className="text-champagne mr-2">“</span>
              {t.home.quoteText}
              <span aria-hidden="true" className="text-champagne ml-2">”</span>
            </p>
            <span className="block w-12 h-px bg-champagne mx-auto mt-10 relative z-10" />
          </div>
        </div>
      </section>

      {/* STRATEGIC SOLUTIONS */}
      <section
        data-testid="solutions-section"
        className="container-x py-28 md:py-36"
      >
        <div className="text-center mb-20">
          <span className="eyebrow mb-6 block">{t.home.solutionsEyebrow}</span>
          <h2 className="display-h2 max-w-[28ch] mx-auto">
            {accent(t.home.solutionsTitle)}
          </h2>
          <div className="section-divider mt-8" />
          <p className="body-lg mt-8 max-w-2xl mx-auto">
            {t.home.solutionsBody}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
          {[
            { n: "01", t: t.home.f1Title, b: t.home.f1Body, img: IMAGERY.cranes, key: "deal_origination" },
            { n: "02", t: t.home.f2Title, b: t.home.f2Body, img: IMAGERY.grid, key: "ma_advisory" },
            { n: "03", t: t.home.f3Title, b: t.home.f3Body, img: IMAGERY.skyline, key: "market_entry" },
          ].map((f, i) => (
            <Link
              key={i}
              to="/solutions"
              onClick={() =>
                trackEvent("solution_card_click", {
                  card_id: f.key,
                  card_title: f.t,
                  position: i + 1,
                })
              }
              data-testid={`feature-card-${i + 1}`}
              className="card-premium p-10 rounded-sm relative overflow-hidden group block"
            >
              <div className="relative z-10">
                <span className="font-mono text-[11px] tracking-[0.22em] text-champagne block mb-6">
                  {f.n}
                </span>
                <h3 className="font-serif text-2xl text-white mb-5 max-w-[20ch]">
                  {f.t}
                </h3>
                <p className="text-[#C8CDD8] text-[15px] leading-relaxed">
                  {f.b}
                </p>
                <div className="mt-10 flex items-center gap-3 text-champagne font-sans text-[11px] uppercase tracking-[0.2em] font-medium">
                  <span className="block w-8 h-px bg-current" />
                  <span>
                    {i === 0 ? "Origination" : i === 1 ? "M&A" : "Entry"}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY US (2-col detail) */}
      <section
        data-testid="why-section"
        className="relative bg-obsidian-700 py-28 md:py-36 overflow-hidden"
      >
        <div className="container-x grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32">
            <p className="eyebrow">{t.home.whyEyebrow}</p>
            <h2 className="display-h2 mt-5 max-w-[14ch] text-white">
              {accent(t.home.whyTitle)}
            </h2>
            <div className="mt-10 relative aspect-[4/5] overflow-hidden rounded-sm">
              <img
                src={IMAGERY.excellenceSP}
                alt="São Paulo financial district at dusk"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: "brightness(0.55) saturate(0.85) contrast(1.15) hue-rotate(-8deg)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
              <div className="absolute inset-0 ring-1 ring-inset ring-champagne/15" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="font-serif italic text-2xl leading-tight">
                  {t.home.whyQuote}
                </p>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6 lg:col-start-7">
            <ol className="divide-y divide-white/8 border-y border-white/8">
              {[
                { t: t.home.why1Title, b: t.home.why1Body },
                { t: t.home.why2Title, b: t.home.why2Body },
                { t: t.home.why3Title, b: t.home.why3Body },
              ].map((row, i) => (
                <li
                  key={i}
                  data-testid={`why-row-${i + 1}`}
                  className="grid grid-cols-12 gap-6 py-10 group"
                >
                  <span className="col-span-2 font-mono text-[11px] tracking-[0.22em] text-champagne pt-2">
                    0{i + 1}
                  </span>
                  <div className="col-span-10">
                    <h3 className="display-h3 text-white">{row.t}</h3>
                    <p className="mt-4 body-lg max-w-xl">{row.b}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section
        data-testid="home-closing-cta"
        className="relative bg-obsidian text-white overflow-hidden grid-pattern"
      >
        <img
          src={IMAGERY.port}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/85 to-obsidian/60" />
        <div className="container-x relative z-10 py-28 md:py-40 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-10">
            <p className="eyebrow-light">{t.home.ctaEyebrow}</p>
            <h2 className="display-h2 mt-5 text-white max-w-[18ch]">
              {accent(t.home.ctaTitle)}
            </h2>
            <div className="section-divider mt-8 mr-auto ml-0" />
            <div className="mt-10">
              <Link
                to="/contact"
                data-testid="home-closing-cta-btn"
                className="group gold-btn"
              >
                {t.home.ctaButton}
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
